const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1/";

/**
 * Streams an assistant response over Server-Sent Events.
 *
 * The backend emits three event types:
 *   - status: { text }  → progress while tools run ("Fetching sales summary…")
 *   - delta:  { text }  → a chunk of the final markdown answer (token-ish)
 *   - error:  { message }
 * and ends the stream after a `done` event.
 *
 * @param {Array} messages  prior conversation as [{role, content}]
 * @param {object} handlers { onStatus(text), onDelta(text), signal }
 */
export async function streamMessage(messages, { onStatus, onDelta, signal } = {}) {
  const res = await fetch(`${apiUrl}assistant/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages }),
    signal,
  });

  // Errors before the stream opens come back as normal JSON (e.g. 503 not configured).
  if (!res.ok || !res.body) {
    let message = "The assistant is unavailable right now.";
    try {
      const j = await res.json();
      message = j.message || message;
    } catch {
      /* keep default */
    }
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      let event = "message";
      let data = "";
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!data) continue;

      let payload;
      try {
        payload = JSON.parse(data);
      } catch {
        continue;
      }

      if (event === "delta") onDelta?.(payload.text || "");
      else if (event === "status") onStatus?.(payload.text || "");
      else if (event === "error") throw new Error(payload.message || "Assistant error");
      // 'done' → loop ends when the reader closes
    }
  }
}
